import React, { memo, useCallback, useRef } from 'react'

import {
  TtTrayItem,
  TtTrayChyronSpeaker,
  TtDropdown,
  TtIconButton,
  TtMenu,
  TtMenuItem,
} from '@23/react'
import { useSortable } from '@dnd-kit/sortable'
import { intl } from 'lang/intl'
import {
  useOverlayState,
  setChyron,
  setSpeakerInfoChyron,
} from '../../../firebase/hooks/useOverlays'
import { Speaker } from '../../../sharedLegacy/hooks/useSpeakersQuery'
import Toolbar from './Toolbar'
import GutterMenu from './GutterMenu'
import useTrayTracking from './useTrayTracking'
import { useScrollRegister, useScroll } from './ScrollProvider'
import { TrayCardChyronSpeaker } from '../../../firebase/model/tray-cards/types/chyron-speaker-model'
import { ChyronType } from '../../../firebase/model/chyron-model'
import {
  setActiveCard,
  removeCard,
  updateChyronSpeakerCard,
} from '../../../firebase/hooks/useTray'

interface ChyronSpeakerCardProps {
  isActiveCard: boolean
  isPriorToSelectedCard: boolean
  isLastCard: boolean
  position: number
  card: TrayCardChyronSpeaker
  speakers: Speaker[]
}

const ChyronSpeakerCard = ({
  isActiveCard,
  isPriorToSelectedCard,
  isLastCard,
  position,
  card,
  speakers,
}: ChyronSpeakerCardProps): JSX.Element => {
  const overlay = useOverlayState()
  const trayTracking = useTrayTracking()
  const speaker = speakers.find((s) => s.id === card.speakerId)
  const ref = useRef<HTMLTtTrayItemElement>(null)
  useScrollRegister(card.id, position)
  const { scrollIntoView } = useScroll()

  const { setNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({
      id: card.id,
    })

  const style = isSorting
    ? {
        transform: `translateY(${transform?.y ?? 0}px)`,
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.8 : 1,
      }
    : {}

  const onSpeakerMenuSelectHandler = (speakerId: string) => {
    updateChyronSpeakerCard(card.id, speakerId)
  }

  const isChyronCurrentlyShowing =
    !!overlay &&
    !!speaker &&
    overlay.type === ChyronType.SpeakerInfo &&
    overlay.liveSpeakerId === speaker.id

  const onCardClickHandler = () => {
    if (speaker) {
      if (!isChyronCurrentlyShowing) {
        setSpeakerInfoChyron(
          speaker.id,
          speaker.fullName,
          speaker.title,
          speaker.avatarURL,
          speaker.bio,
          speaker.company
        )
      }
      trayTracking.activated(card.id)

      setActiveCard(card.id)
      scrollIntoView(card.id)
    }
  }

  const onRemoveChyronClickHandler = () => {
    setChyron(null)
  }

  const onDeleteCard = () => {
    if (isChyronCurrentlyShowing) {
      setChyron(null)
    }
    trayTracking.deleted(card.id)
    removeCard(card.id)
  }

  const getGutterMode = useCallback(() => {
    if (isSorting) return 'disabled'
    if (isLastCard) return 'persisted'
    return 'normal'
  }, [isLastCard, isSorting])

  setNodeRef(ref.current)

  return (
    <TtTrayItem
      data-id={card.id}
      ref={ref}
      style={style}
      gutterDelay={400}
      gutterMode={getGutterMode()}
    >
      <TtTrayChyronSpeaker
        tag={intl.formatMessage({ defaultMessage: 'Speaker' })}
        name={
          !speaker
            ? intl.formatMessage({ defaultMessage: 'Name' })
            : speaker.fullName
        }
        jobTitle={
          !speaker
            ? intl.formatMessage({ defaultMessage: 'Title' })
            : speaker.title
        }
        company={
          !speaker
            ? intl.formatMessage({ defaultMessage: 'Company' })
            : speaker.company
        }
        bio={speaker?.bio}
        imageUrl={speaker?.avatarURL}
        selected={isActiveCard}
        disabled={isPriorToSelectedCard}
        onTt-click={(event) => {
          if (event.target === event.currentTarget) {
            onCardClickHandler()
          }
        }}
      >
        {card.speakerId && (
          <>
            {isChyronCurrentlyShowing ? (
              <TtIconButton
                slot="toolbar"
                library="one/webinars"
                name="visibility-off"
                label="Visibility off"
                onClick={onRemoveChyronClickHandler}
                className="visibility-icon"
              />
            ) : (
              <TtIconButton
                slot="toolbar"
                library="one/webinars"
                name="visibility-on"
                label="Visibility on"
                onClick={onCardClickHandler}
                className="visibility-icon"
              />
            )}
          </>
        )}
        {speakers.length > 0 && (
          <TtDropdown
            slot="toolbar"
            placement="bottom-start"
            closeOnSelect
            hoist
          >
            <TtIconButton
              slot="trigger"
              library="one/webinars"
              name="speaker"
              label="Toolbar Menu"
            />
            <TtMenu
              onTt-select={(e) =>
                onSpeakerMenuSelectHandler(e.detail.item.value)
              }
            >
              {speakers.map((s) => (
                <TtMenuItem key={s.id} value={s.id}>
                  {s.fullName}
                </TtMenuItem>
              ))}
            </TtMenu>
          </TtDropdown>
        )}
        <Toolbar cardId={card.id} onDeleteClick={onDeleteCard} />
      </TtTrayChyronSpeaker>
      <GutterMenu cardId={card.id} />
    </TtTrayItem>
  )
}

export default memo(ChyronSpeakerCard)
